"use client";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";

interface FormData {
  name: string;
  surname: string;
  phone: string;
  email: string;
  businessName: string;
  organisationCategory: string;
  agreeTerms: boolean;
}

interface FormErrors {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  businessName?: string;
  organisationCategory?: string;
  agreeTerms?: string;
}

interface RegistrationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ReferralProgramPayload {
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  consultant_name: string;
  organisation_category: string;
}

interface ReferralProgramResponse {
  id: number | string;
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  consultant_name: string;
  organisation_category: string;
}

const categories = [
  { label: "Funeral Parlour", value: "funeral_parlor" },
  {
    label: "Church / Religious Organisation",
    value: "church_religious_organisation",
  },
  { label: "Community Organisation", value: "community_organisation" },
  { label: "Insurance Company", value: "insurance_company" },
  { label: "Individual Consultant", value: "individual_consultant" },
  { label: "Other", value: "other" },
];

export default function RegistrationForm({
  open,
  onClose,
  onSuccess,
}: RegistrationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    surname: "",
    phone: "+263",
    email: "",
    businessName: "",
    organisationCategory: "",
    agreeTerms: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      surname: "",
      phone: "+263",
      email: "",
      businessName: "",
      organisationCategory: "",
      agreeTerms: false,
    });
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.surname.trim()) newErrors.surname = "Surname is required";

    if (!formData.phone.trim() || formData.phone === "+263") {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!formData.businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!formData.organisationCategory) {
      newErrors.organisationCategory = "Select a category";
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = "You must agree to the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);

    const payload: ReferralProgramPayload = {
      name: formData.name.trim(),
      surname: formData.surname.trim(),
      email: formData.email.trim(),
      phone_number: formData.phone.trim(),
      consultant_name: formData.businessName.trim(),
      organisation_category: formData.organisationCategory,
    };

    try {
      const response = await apiService.post<ReferralProgramResponse>(
        "/api/refferal_program/refferal_programs/",
        payload,
      );

      console.log("Referral program created:", response.data);

      toast.success("Registration submitted successfully");
      resetForm();
      onSuccess();
    } catch (err: unknown) {
      console.error("Failed to submit referral registration", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 1, md: 2 },
          maxHeight: "90vh",
          border: "1px solid #724E41",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#6D4C41",
            fontSize: { xs: "1.3rem", md: "1.6rem" },
          }}
        >
          Register
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#3E2723" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
              >
                Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Your name here"
                size="small"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F0F4F8",
                    borderRadius: "16px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
              >
                Surname *
              </Typography>
              <TextField
                fullWidth
                placeholder="Placeholder"
                size="small"
                value={formData.surname}
                onChange={(e) => handleChange("surname", e.target.value)}
                error={!!errors.surname}
                helperText={errors.surname}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F0F4F8",
                    borderRadius: "16px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
              >
                Phone Number *
              </Typography>
              <TextField
                fullWidth
                placeholder="+263"
                size="small"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F0F4F8",
                    borderRadius: "16px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
              >
                Email *
              </Typography>
              <TextField
                fullWidth
                placeholder="Placeholder"
                size="small"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F0F4F8",
                    borderRadius: "16px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
            >
              Business / Consultant Name *
            </Typography>
            <TextField
              fullWidth
              placeholder="Placeholder"
              size="small"
              value={formData.businessName}
              onChange={(e) => handleChange("businessName", e.target.value)}
              error={!!errors.businessName}
              helperText={errors.businessName}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F0F4F8",
                  borderRadius: "16px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#6D4C41", mb: 0.5 }}
            >
              Organisation Category *
            </Typography>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.organisationCategory}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F0F4F8",
                  borderRadius: "16px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                },
              }}
            >
              <Select
                displayEmpty
                value={formData.organisationCategory}
                onChange={(e) =>
                  handleChange("organisationCategory", e.target.value)
                }
                variant="outlined"
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: "#A0938D" }}>
                        Placeholder
                      </Typography>
                    );
                  }

                  const selectedCategory = categories.find(
                    (cat) => cat.value === selected,
                  );

                  return selectedCategory?.label || selected;
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>

              {errors.organisationCategory && (
                <FormHelperText>{errors.organisationCategory}</FormHelperText>
              )}
            </FormControl>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeTerms}
                  onChange={(e) => handleChange("agreeTerms", e.target.checked)}
                  sx={{
                    color: "#8D6E63",
                    "&.Mui-checked": { color: "#6D4C41" },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: "#5D4037", fontSize: "0.82rem" }}
                >
                  I agree to the partnership terms and Muzukuru&apos;s privacy
                  policy. I understand that Muzukuru will contact me to activate
                  my partnership account.
                </Typography>
              }
            />
            {errors.agreeTerms && (
              <FormHelperText error sx={{ ml: 4 }}>
                {errors.agreeTerms}
              </FormHelperText>
            )}
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#724E41",
                px: 6,
                py: 1.5,
                minWidth: 160,
                borderRadius: "24px",
                "&:hover": { backgroundColor: "#4E342E" },
                "&.Mui-disabled": {
                  backgroundColor: "#A1887F",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting ? "Submitting..." : "Register"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
